import React, { useState, useEffect } from 'react'
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  FlagIcon,
  CheckCircleIcon,
  SunIcon,
  UserIcon,
  BoltIcon
} from '@heroicons/react/24/outline'
import api from '../utils/api'
import { useAuth } from '../contexts/AuthContext'
import {
  getCurrentJalali,
  getJalaliMonthName,
  getJalaliDaysInMonth,
  getJalaliFirstDayOfWeek,
  jalaliToGregorianStr,
  formatHoursAndMinutes
} from '../utils/jalali'
import { ConfirmModal, AlertModal } from '../components/ConfirmModal'

interface Attendance {
  id: number
  date: string
  check_in: string | null
  check_out: string | null
  work_hours: number
}

interface Leave {
  id: number
  date: string
  type: string
  is_hourly?: boolean
  hours?: number
  reason?: string
}

interface Overtime {
  id: number
  date: string
  hours: number
  reason?: string
}

interface Todo {
  id: number
  date: string
  title: string
  is_done: boolean
}

interface Holiday {
  date: string
  name: string
  is_custom?: boolean
}

interface SimpleUser {
  id: number
  name: string
  username?: string
}

export const Calendar: React.FC = () => {
  const { user, isAdmin } = useAuth()
  const currentJalali = getCurrentJalali()
  const [currentYear, setCurrentYear] = useState(currentJalali.jy)
  const [currentMonth, setCurrentMonth] = useState(currentJalali.jm)
  
  // Admin user switcher
  const [allUsers, setAllUsers] = useState<SimpleUser[]>([])
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

  const [attendances, setAttendances] = useState<Attendance[]>([])
  const [leaves, setLeaves] = useState<Leave[]>([])
  const [overtimes, setOvertimes] = useState<Overtime[]>([])
  const [todos, setTodos] = useState<Todo[]>([])
  const [holidays, setHolidays] = useState<Holiday[]>([])
  
  const [selectedDay, setSelectedDay] = useState<{ day: number; dateStr: string; isFriday: boolean; holidayName?: string; isCustomHoliday?: boolean } | null>(null)
  const [showAddAttendance, setShowAddAttendance] = useState(false)
  const [showAddOvertime, setShowAddOvertime] = useState(false)
  const [showHolidayModal, setShowHolidayModal] = useState(false)
  const [holidayNameInput, setHolidayNameInput] = useState('')

  // Dialogs
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    confirmVariant?: 'danger' | 'primary' | 'warning'
    onConfirm: () => void
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmVariant: 'danger',
    onConfirm: () => {}
  })

  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean
    title?: string
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  // Manual attendance form
  const [inHour, setInHour] = useState('09')
  const [inMin, setInMin] = useState('00')
  const [outHour, setOutHour] = useState('17')
  const [outMin, setOutMin] = useState('00')

  // Extra time (Overtime) form
  const [otHours, setOtHours] = useState('1')
  const [otMinutes, setOtMinutes] = useState('00')
  const [otReason, setOtReason] = useState('')
  
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Fetch users list for admin switcher
  useEffect(() => {
    if (isAdmin) {
      api.get('/admin/users')
        .then(res => {
          if (Array.isArray(res.data)) {
            setAllUsers(res.data)
          }
        })
        .catch(() => {})
    }
  }, [isAdmin])

  // Fetch month data
  const fetchData = async () => {
    setLoading(true)
    try {
      const daysCount = getJalaliDaysInMonth(currentYear, currentMonth)
      const startStr = jalaliToGregorianStr(currentYear, currentMonth, 1)
      const endStr = jalaliToGregorianStr(currentYear, currentMonth, daysCount)

      const uidParam = selectedUserId ? `&user_id=${selectedUserId}` : ''

      const [attRes, leavesRes, otRes, todosRes, holidaysRes] = await Promise.allSettled([
        api.get(`/attendance/history?start_date=${startStr}&end_date=${endStr}${uidParam}`),
        api.get(`/attendance/leaves?start_date=${startStr}&end_date=${endStr}${uidParam}`),
        api.get(`/attendance/overtime?start_date=${startStr}&end_date=${endStr}${uidParam}`),
        api.get(`/todos`),
        api.get(`/attendance/calendar/holidays?start_date=${startStr}&end_date=${endStr}`)
      ])

      if (attRes.status === 'fulfilled' && Array.isArray(attRes.value.data)) {
        setAttendances(attRes.value.data)
      } else {
        setAttendances([])
      }

      if (leavesRes.status === 'fulfilled' && Array.isArray(leavesRes.value.data)) {
        setLeaves(leavesRes.value.data)
      } else {
        setLeaves([])
      }

      if (otRes.status === 'fulfilled' && Array.isArray(otRes.value.data)) {
        setOvertimes(otRes.value.data)
      } else {
        setOvertimes([])
      }

      if (todosRes.status === 'fulfilled' && Array.isArray(todosRes.value.data)) {
        setTodos(todosRes.value.data)
      } else {
        setTodos([])
      }

      if (holidaysRes.status === 'fulfilled' && Array.isArray(holidaysRes.value.data)) {
        setHolidays(holidaysRes.value.data)
      } else {
        setHolidays([])
      }
    } catch (err) {
      console.error('Error fetching calendar data:', err)
      setAttendances([])
      setLeaves([])
      setOvertimes([])
      setTodos([])
      setHolidays([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [currentYear, currentMonth, selectedUserId])

  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12)
      setCurrentYear(currentYear - 1)
    } else {
      setCurrentMonth(currentMonth - 1)
    }
  }

  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1)
      setCurrentYear(currentYear + 1)
    } else {
      setCurrentMonth(currentMonth + 1)
    }
  }

  const goToToday = () => {
    setCurrentYear(currentJalali.jy)
    setCurrentMonth(currentJalali.jm)
  }

  // Days in month & offset
  const totalDays = getJalaliDaysInMonth(currentYear, currentMonth)
  const firstDayOfWeek = getJalaliFirstDayOfWeek(currentYear, currentMonth)
  const offset = (firstDayOfWeek + 1) % 7

  const weekDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']

  // Handle Save Manual Attendance
  const handleSaveAttendance = async () => {
    if (!selectedDay) return
    setSubmitting(true)
    try {
      const cin = `${inHour.padStart(2, '0')}:${inMin.padStart(2, '0')}`
      const cout = `${outHour.padStart(2, '0')}:${outMin.padStart(2, '0')}`
      
      const inMinutes = parseInt(inHour) * 60 + parseInt(inMin)
      const outMinutes = parseInt(outHour) * 60 + parseInt(outMin)
      const diffHours = Math.max(0, (outMinutes - inMinutes) / 60)

      await api.post('/attendance/manual', {
        date: selectedDay.dateStr,
        check_in: cin,
        check_out: cout,
        work_hours: parseFloat(diffHours.toFixed(2)),
        user_id: selectedUserId || undefined
      })

      setShowAddAttendance(false)
      fetchData()
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'ثبت موفق',
        message: 'تردد دستی با موفقیت ثبت شد.'
      })
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: err.response?.data?.detail || 'خطا در ثبت تردد دستی'
      })
    } finally {
      setSubmitting(false)
    }
  }

  // Handle Save Extra Time / Overtime
  const handleSaveOvertime = async () => {
    if (!selectedDay) return
    const totalOtHours = parseFloat(otHours) + (parseFloat(otMinutes) / 60)
    if (totalOtHours <= 0) {
      setAlertDialog({
        isOpen: true,
        type: 'info',
        title: 'توجه',
        message: 'لطفاً مدت زمان معتبری برای اضافه‌کاری انتخاب کنید.'
      })
      return
    }

    setSubmitting(true)
    try {
      await api.post('/attendance/overtime', {
        date: selectedDay.dateStr,
        hours: parseFloat(totalOtHours.toFixed(2)),
        reason: otReason.trim() || undefined,
        user_id: selectedUserId || undefined
      })

      setShowAddOvertime(false)
      setOtReason('')
      fetchData()
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'ثبت اضافه‌کاری',
        message: `${formatHoursAndMinutes(totalOtHours)} اضافه‌کاری با موفقیت ثبت شد.`
      })
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: err.response?.data?.detail || 'خطا در ثبت اضافه‌کاری'
      })
    } finally {
      setSubmitting(false)
    }
  }

  // Delete attendance record
  const handleDeleteAttendance = (id: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'حذف تردد',
      message: 'آیا از حذف این رکورد تردد مطمئن هستید؟ این عملیات قابل بازگشت نیست.',
      confirmVariant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        try {
          await api.delete(`/attendance/${id}`)
          fetchData()
          setAlertDialog({
            isOpen: true,
            type: 'success',
            title: 'حذف شد',
            message: 'رکورد تردد با موفقیت حذف گردید.'
          })
        } catch (err: any) {
          setAlertDialog({
            isOpen: true,
            type: 'error',
            title: 'خطا در حذف',
            message: err.response?.data?.detail || 'خطا در حذف تردد'
          })
        }
      }
    })
  }

  // Delete overtime record
  const handleDeleteOvertime = (id: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'حذف اضافه‌کاری',
      message: 'آیا از حذف این رکورد اضافه‌کاری اطمینان دارید؟',
      confirmVariant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        try {
          await api.delete(`/attendance/overtime/${id}`)
          fetchData()
          setAlertDialog({
            isOpen: true,
            type: 'success',
            title: 'حذف شد',
            message: 'اضافه‌کاری با موفقیت حذف شد.'
          })
        } catch (err: any) {
          setAlertDialog({
            isOpen: true,
            type: 'error',
            title: 'خطا در حذف',
            message: err.response?.data?.detail || 'خطا در حذف اضافه‌کاری'
          })
        }
      }
    })
  }

  // Delete leave record
  const handleDeleteLeave = (id: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'حذف مرخصی',
      message: 'آیا از حذف این مرخصی مطمئن هستید؟',
      confirmVariant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        try {
          await api.delete(`/attendance/leaves/${id}`)
          fetchData()
          setAlertDialog({
            isOpen: true,
            type: 'success',
            title: 'حذف شد',
            message: 'مرخصی با موفقیت حذف گردید.'
          })
        } catch (err: any) {
          setAlertDialog({
            isOpen: true,
            type: 'error',
            title: 'خطا در حذف',
            message: err.response?.data?.detail || 'خطا در حذف مرخصی'
          })
        }
      }
    })
  }

  // Toggle/Set Custom Holiday
  const handleSetCustomHoliday = async () => {
    if (!selectedDay) return
    if (!holidayNameInput.trim()) {
      setAlertDialog({
        isOpen: true,
        type: 'info',
        title: 'توجه',
        message: 'لطفاً عنوان یا مناسبت تعطیلی را وارد کنید.'
      })
      return
    }
    setSubmitting(true)
    try {
      await api.post('/attendance/calendar/holidays', {
        date: selectedDay.dateStr,
        name: holidayNameInput.trim()
      })
      setShowHolidayModal(false)
      const newName = holidayNameInput.trim()
      setHolidayNameInput('')
      await fetchData()
      setSelectedDay(prev => prev ? { ...prev, holidayName: newName, isCustomHoliday: true } : null)
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'تعطیلی ثبت شد',
        message: `روز به عنوان تعطیل (${newName}) ثبت شد.`
      })
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: err.response?.data?.detail || 'خطا در ذخیره تعطیلی'
      })
    } finally {
      setSubmitting(false)
    }
  }

  // Remove Custom Holiday
  const handleRemoveCustomHoliday = () => {
    if (!selectedDay) return
    setConfirmDialog({
      isOpen: true,
      title: 'لغو تعطیلی',
      message: 'آیا از لغو تعطیلی این روز اطمینان دارید؟',
      confirmVariant: 'warning',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        setSubmitting(true)
        try {
          await api.delete(`/attendance/calendar/holidays/${selectedDay.dateStr}`)
          await fetchData()
          setSelectedDay(prev => prev ? { ...prev, holidayName: undefined, isCustomHoliday: false } : null)
          setAlertDialog({
            isOpen: true,
            type: 'success',
            title: 'لغو شد',
            message: 'تعطیلی این روز لغو شد.'
          })
        } catch (err: any) {
          setAlertDialog({
            isOpen: true,
            type: 'error',
            title: 'خطا',
            message: err.response?.data?.detail || 'خطا در لغو تعطیلی'
          })
        } finally {
          setSubmitting(false)
        }
      }
    })
  }

  const hoursOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
  const otHoursOptions = Array.from({ length: 13 }, (_, i) => i.toString())
  const minsOptions = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '41', '45', '50', '55']
  const otMinsOptions = ['00', '15', '30', '45']

  return (
    <div className="space-y-6 pb-20">
      {/* Header & Controls */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-2xl">
            <CalendarDaysIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-white">
              تقویم و کارکرد ماهانه
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              مشاهده و ویرایش ترددها، اضافه‌کاری، تعطیلات و مرخصی‌ها
            </p>
          </div>
        </div>

        {/* Controls: Admin User Switcher + Month Selector */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Admin User Switcher Dropdown */}
          {isAdmin && allUsers.length > 0 && (
            <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-900 px-3 py-1.5 rounded-2xl border border-slate-200 dark:border-slate-700">
              <UserIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <select
                value={selectedUserId || ''}
                onChange={e => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
                className="bg-transparent text-xs font-bold text-slate-800 dark:text-slate-100 outline-none cursor-pointer"
              >
                <option value="" className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">حساب خودم ({user?.name})</option>
                {allUsers.filter(u => u.id !== user?.id).map(u => (
                  <option key={u.id} value={u.id} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{u.name} ({u.username || u.id})</option>
                ))}
              </select>
            </div>
          )}

          {/* Month Selector */}
          <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-900/50 p-1.5 rounded-2xl border border-slate-200/80 dark:border-slate-700">
            <button
              onClick={prevMonth}
              className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-xl text-slate-600 dark:text-slate-300 transition shadow-sm"
              title="ماه قبل"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>

            <span className="text-base font-bold text-slate-800 dark:text-slate-100 min-w-[140px] text-center">
              {getJalaliMonthName(currentMonth)} {currentYear}
            </span>

            <button
              onClick={nextMonth}
              className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-xl text-slate-600 dark:text-slate-300 transition shadow-sm"
              title="ماه بعد"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>

            <button
              onClick={goToToday}
              className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition"
            >
              امروز
            </button>
          </div>
        </div>
      </div>

      {/* Main Calendar Grid */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60">
        {/* Week Days Header */}
        <div className="grid grid-cols-7 gap-2 mb-3 text-center">
          {weekDays.map((w, idx) => (
            <div
              key={idx}
              className={`py-2 text-xs font-bold ${
                idx === 6 ? 'text-rose-500' : 'text-slate-400 dark:text-slate-500'
              }`}
            >
              {w}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-2">
          {/* Empty cells for offset */}
          {Array.from({ length: offset }).map((_, idx) => (
            <div key={`empty-${idx}`} className="h-20 sm:h-24 rounded-2xl bg-slate-50/40 dark:bg-slate-900/20 border border-dashed border-slate-100 dark:border-slate-800/40" />
          ))}

          {/* Days in Month */}
          {Array.from({ length: totalDays }).map((_, idx) => {
            const dayNum = idx + 1
            const dateStr = jalaliToGregorianStr(currentYear, currentMonth, dayNum)
            
            const dayOfWeek = (offset + idx) % 7
            const isFriday = dayOfWeek === 6
            const isToday =
              currentYear === currentJalali.jy &&
              currentMonth === currentJalali.jm &&
              dayNum === currentJalali.jd

            const dayHoliday = holidays.find(h => h.date === dateStr)
            
            let holidayTitle: string | undefined = undefined
            if (isFriday) {
              holidayTitle = 'جمعه'
            } else if (dayHoliday && dayHoliday.name !== 'جمعه') {
              holidayTitle = dayHoliday.name
            }

            const isCustomH = dayHoliday?.is_custom || false

            const dayAtts = attendances.filter(a => a.date === dateStr)
            const dayLeaves = leaves.filter(l => l.date === dateStr)
            const dayOts = overtimes.filter(o => o.date === dateStr)
            const dayTodos = todos.filter(t => t.date === dateStr)

            const totalHours = dayAtts.reduce((sum, a) => sum + (a.work_hours || 0), 0)
            const totalOtHours = dayOts.reduce((sum, o) => sum + (o.hours || 0), 0)

            return (
              <div
                key={dayNum}
                onClick={() => {
                  setSelectedDay({
                    day: dayNum,
                    dateStr,
                    isFriday,
                    holidayName: holidayTitle,
                    isCustomHoliday: isCustomH
                  })
                  setShowAddAttendance(false)
                  setShowAddOvertime(false)
                }}
                className={`relative min-h-[5.5rem] sm:min-h-[6.5rem] p-2 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between select-none ${
                  isToday
                    ? 'border-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/20 shadow-sm ring-2 ring-emerald-500/20'
                    : holidayTitle
                    ? 'border-rose-200 dark:border-rose-900/40 bg-rose-50/40 dark:bg-rose-950/20 hover:border-rose-300'
                    : 'border-slate-100 dark:border-slate-700/60 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
              >
                {/* Top Row: Day Number & Badges */}
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm font-bold w-6 h-6 flex items-center justify-center rounded-lg ${
                      isToday
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : holidayTitle
                        ? 'text-rose-600 dark:text-rose-400 font-extrabold'
                        : 'text-slate-700 dark:text-slate-200'
                    }`}
                  >
                    {dayNum}
                  </span>

                  {/* Holiday or Custom Badge */}
                  {holidayTitle && (
                    <span className="text-[10px] truncate max-w-[65px] px-1.5 py-0.5 rounded-md bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-300 font-semibold" title={holidayTitle}>
                      {holidayTitle}
                    </span>
                  )}
                </div>

                {/* Middle / Bottom Content */}
                <div className="space-y-1 my-1">
                  {/* Worked Hours */}
                  {totalHours > 0 && (
                    <div className="flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded-md">
                      <ClockIcon className="w-3 h-3" />
                      <span>{formatHoursAndMinutes(totalHours)}</span>
                    </div>
                  )}

                  {/* Overtime Badge */}
                  {totalOtHours > 0 && (
                    <div className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-1.5 py-0.5 rounded-md truncate">
                      <BoltIcon className="w-3 h-3 flex-shrink-0" />
                      <span>+{formatHoursAndMinutes(totalOtHours)} اضافه</span>
                    </div>
                  )}

                  {/* Leave Badge */}
                  {dayLeaves.length > 0 && (
                    <div className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-1 py-0.5 rounded-md truncate">
                      <SunIcon className="w-2.5 h-2.5 flex-shrink-0" />
                      <span className="truncate">
                        {dayLeaves[0].is_hourly ? `${dayLeaves[0].hours}h ساعتی` : 'مرخصی'}
                      </span>
                    </div>
                  )}

                  {/* Todo Badge */}
                  {dayTodos.length > 0 && (
                    <div className="flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-1 py-0.5 rounded-md">
                      <CheckCircleIcon className="w-2.5 h-2.5 flex-shrink-0" />
                      <span>{dayTodos.length} تسک</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Selected Day Details Drawer / Modal */}
      {selectedDay && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-5 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded-xl">
                  <CalendarDaysIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800 dark:text-white">
                    {selectedDay.day} {getJalaliMonthName(currentMonth)} {currentYear}
                  </h3>
                  <p className="text-xs text-slate-400">{selectedDay.dateStr}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedDay(null)
                  setShowAddAttendance(false)
                  setShowAddOvertime(false)
                }}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Holiday Info & Toggle Button */}
            <div className="bg-slate-50 dark:bg-slate-900/50 p-3.5 rounded-2xl border border-slate-200/80 dark:border-slate-700 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FlagIcon className={`w-4 h-4 ${selectedDay.holidayName ? 'text-rose-500' : 'text-slate-400'}`} />
                <div>
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    وضعیت تعطیلی:{' '}
                  </span>
                  <span className={`text-xs font-bold ${selectedDay.holidayName ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500'}`}>
                    {selectedDay.holidayName ? `تعطیل (${selectedDay.holidayName})` : 'روز عادی کاری'}
                  </span>
                </div>
              </div>

              {/* Holiday Toggle / Edit Button */}
              {selectedDay.isCustomHoliday ? (
                <button
                  onClick={handleRemoveCustomHoliday}
                  disabled={submitting}
                  className="text-xs px-3 py-1.5 bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-300 rounded-xl font-medium hover:bg-rose-100 dark:hover:bg-rose-900/60 transition border border-rose-200 dark:border-rose-800"
                >
                  لغو تعطیلی
                </button>
              ) : !selectedDay.isFriday ? (
                <button
                  onClick={() => {
                    setHolidayNameInput(selectedDay.holidayName || 'تعطیلی شرکت')
                    setShowHolidayModal(true)
                  }}
                  className="text-xs px-3 py-1.5 bg-rose-600 text-white rounded-xl font-medium hover:bg-rose-700 transition flex items-center gap-1"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  تنظیم تعطیلی
                </button>
              ) : null}
            </div>

            {/* Attendances Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                  <ClockIcon className="w-3.5 h-3.5 text-emerald-500" />
                  رکوردهای تردد
                </h4>
                <button
                  onClick={() => {
                    setShowAddAttendance(!showAddAttendance)
                    setShowAddOvertime(false)
                  }}
                  className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold hover:underline flex items-center gap-1"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  ثبت تردد جدید
                </button>
              </div>

              {/* Add Attendance Box */}
              {showAddAttendance && (
                <div className="bg-emerald-50/50 dark:bg-emerald-950/20 p-4 rounded-2xl border border-emerald-200 dark:border-emerald-800/40 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Check-in */}
                    <div className="space-y-1">
                      <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                        ساعت ورود
                      </label>
                      <div className="flex items-center gap-1">
                        <select
                          value={inHour}
                          onChange={e => setInHour(e.target.value)}
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100"
                        >
                          {hoursOptions.map(h => (
                            <option key={h} value={h} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{h}</option>
                          ))}
                        </select>
                        <span className="text-slate-400">:</span>
                        <select
                          value={inMin}
                          onChange={e => setInMin(e.target.value)}
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100"
                        >
                          {minsOptions.map(m => (
                            <option key={m} value={m} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{m}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Check-out */}
                    <div className="space-y-1">
                      <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                        ساعت خروج
                      </label>
                      <div className="flex items-center gap-1">
                        <select
                          value={outHour}
                          onChange={e => setOutHour(e.target.value)}
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100"
                        >
                          {hoursOptions.map(h => (
                            <option key={h} value={h} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{h}</option>
                          ))}
                        </select>
                        <span className="text-slate-400">:</span>
                        <select
                          value={outMin}
                          onChange={e => setOutMin(e.target.value)}
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100"
                        >
                          {minsOptions.map(m => (
                            <option key={m} value={m} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{m}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      onClick={() => setShowAddAttendance(false)}
                      className="text-xs px-3 py-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                    >
                      انصراف
                    </button>
                    <button
                      onClick={handleSaveAttendance}
                      disabled={submitting}
                      className="text-xs px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl shadow-sm transition"
                    >
                      {submitting ? 'در حال ثبت...' : 'ذخیره تردد'}
                    </button>
                  </div>
                </div>
              )}

              {/* List Attendances */}
              {attendances.filter(a => a.date === selectedDay.dateStr).length === 0 ? (
                <p className="text-xs text-slate-400 py-1">ترددی برای این روز ثبت نشده است.</p>
              ) : (
                <div className="space-y-2">
                  {attendances
                    .filter(a => a.date === selectedDay.dateStr)
                    .map(att => (
                      <div
                        key={att.id}
                        className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700/60"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                            ورود: {att.check_in ? att.check_in.substring(11, 16) || att.check_in : '-'}
                          </span>
                          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                            خروج: {att.check_out ? att.check_out.substring(11, 16) || att.check_out : 'در حال کار'}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 font-bold">
                            {formatHoursAndMinutes(att.work_hours)}
                          </span>
                        </div>

                        <button
                          onClick={() => handleDeleteAttendance(att.id)}
                          className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition"
                          title="حذف تردد"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Overtime (Extra Time) Section */}
            <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider flex items-center gap-1.5">
                  <BoltIcon className="w-3.5 h-3.5 text-indigo-500" />
                  اضافه‌کاری (Extra Time)
                </h4>
                <button
                  onClick={() => {
                    setShowAddOvertime(!showAddOvertime)
                    setShowAddAttendance(false)
                  }}
                  className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold hover:underline flex items-center gap-1"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  ثبت اضافه‌کاری
                </button>
              </div>

              {/* Add Overtime Box */}
              {showAddOvertime && (
                <div className="bg-indigo-50/50 dark:bg-indigo-950/20 p-4 rounded-2xl border border-indigo-200 dark:border-indigo-800/40 space-y-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                      مدت زمان اضافه‌کاری
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 flex items-center gap-1">
                        <select
                          value={otHours}
                          onChange={e => setOtHours(e.target.value)}
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100"
                        >
                          {otHoursOptions.map(h => (
                            <option key={h} value={h} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{h} ساعت</option>
                          ))}
                        </select>
                        <select
                          value={otMinutes}
                          onChange={e => setOtMinutes(e.target.value)}
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100"
                        >
                          {otMinsOptions.map(m => (
                            <option key={m} value={m} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{m} دقیقه</option>
                          ))}
                        </select>
                      </div>

                      {/* Quick Presets */}
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => { setOtHours('1'); setOtMinutes('00'); }}
                          className="text-[10px] px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 hover:border-indigo-500"
                        >
                          ۱ ساعت
                        </button>
                        <button
                          type="button"
                          onClick={() => { setOtHours('2'); setOtMinutes('00'); }}
                          className="text-[10px] px-2 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 hover:border-indigo-500"
                        >
                          ۲ ساعت
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                      توضیحات / علت اضافه‌کاری (اختیاری)
                    </label>
                    <input
                      type="text"
                      value={otReason}
                      onChange={e => setOtReason(e.target.value)}
                      placeholder="مثال: تکمیل تسک، جلسه فنی، ..."
                      className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 outline-none focus:border-indigo-500"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      onClick={() => setShowAddOvertime(false)}
                      className="text-xs px-3 py-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
                    >
                      انصراف
                    </button>
                    <button
                      onClick={handleSaveOvertime}
                      disabled={submitting}
                      className="text-xs px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-sm transition"
                    >
                      {submitting ? 'در حال ثبت...' : 'ذخیره اضافه‌کاری'}
                    </button>
                  </div>
                </div>
              )}

              {/* List Overtimes */}
              {overtimes.filter(o => o.date === selectedDay.dateStr).length === 0 ? (
                <p className="text-xs text-slate-400 py-1">اضافه‌کاری برای این روز ثبت نشده است.</p>
              ) : (
                <div className="space-y-2">
                  {overtimes
                    .filter(o => o.date === selectedDay.dateStr)
                    .map(ot => (
                      <div
                        key={ot.id}
                        className="flex items-center justify-between p-3 rounded-2xl bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-200/60 dark:border-indigo-900/40"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="text-xs px-2.5 py-0.5 rounded-lg bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 font-bold">
                            {formatHoursAndMinutes(ot.hours)} اضافه‌کاری
                          </span>
                          {ot.reason && (
                            <span className="text-xs text-slate-600 dark:text-slate-300 truncate max-w-[200px]">
                              {ot.reason}
                            </span>
                          )}
                        </div>

                        <button
                          onClick={() => handleDeleteOvertime(ot.id)}
                          className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition"
                          title="حذف اضافه‌کاری"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Leaves Section */}
            <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-700">
              <h4 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                <SunIcon className="w-3.5 h-3.5 text-amber-500" />
                مرخصی‌ها
              </h4>
              {leaves.filter(l => l.date === selectedDay.dateStr).length === 0 ? (
                <p className="text-xs text-slate-400 py-1">مرخصی ثبت نشده است.</p>
              ) : (
                <div className="space-y-2">
                  {leaves
                    .filter(l => l.date === selectedDay.dateStr)
                    .map(leave => (
                      <div
                        key={leave.id}
                        className="flex items-center justify-between p-3 rounded-2xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-900/30"
                      >
                        <div>
                          <span className="text-xs font-bold text-amber-800 dark:text-amber-200">
                            {leave.is_hourly ? `مرخصی ساعتی (${leave.hours} ساعت)` : 'مرخصی تمام روز'}
                          </span>
                          {leave.reason && (
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">{leave.reason}</p>
                          )}
                        </div>

                        <button
                          onClick={() => handleDeleteLeave(leave.id)}
                          className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition"
                          title="حذف مرخصی"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Holiday Input Modal */}
      {showHolidayModal && selectedDay && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <FlagIcon className="w-5 h-5 text-rose-500" />
              تنظیم تعطیلی برای {selectedDay.day} {getJalaliMonthName(currentMonth)}
            </h3>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                عنوان / علت تعطیلی:
              </label>
              <input
                type="text"
                value={holidayNameInput}
                onChange={e => setHolidayNameInput(e.target.value)}
                placeholder="مثال: تعطیلی شرکت، مناسبت مذهبی، ..."
                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-200 outline-none focus:border-rose-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowHolidayModal(false)}
                className="text-xs px-3 py-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl"
              >
                انصراف
              </button>
              <button
                onClick={handleSetCustomHoliday}
                disabled={submitting}
                className="text-xs px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-xl transition shadow-sm"
              >
                {submitting ? 'در حال ثبت...' : 'ثبت تعطیلی'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmVariant={confirmDialog.confirmVariant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default Calendar
